import React, { useRef } from 'react';
import { motion, useScroll, useSpring, useReducedMotion } from 'framer-motion';
import { Calendar, MapPin, GraduationCap } from 'lucide-react';
import { formatTenure } from '../utils/tenure';
import { useOnScreen } from '../utils/useOnScreen';
import './Experience.css';
import SectionHeader from './SectionHeader';

const company = {
  name: 'Azentio Software',
  location: 'Navi Mumbai, India',
  initial: 'A'
};

// Newest first. Keep highlights to 3 per role.
const roles = [
  {
    id: 'ai-engineer',
    title: 'AI Engineer',
    duration: 'Aug 2026 - Present',
    current: true,
    description:
      'Part of the team building an enterprise text-to-SQL analytics application, where users ask questions in natural language and get back data, charts, and reports.',
    highlights: [
      'Building multi-agent workflows with LangGraph and LangChain, including intent classification to route each request to the right agent',
      'Developing an evaluation harness to measure answer accuracy and catch regressions across prompt and model changes',
      'Applying AI guardrails and prompt optimization to keep responses safe and reliable'
    ],
    technologies: [
      'Text-to-SQL', 'LangGraph', 'LangChain', 'MCP', 'Multi-Agent Systems', 'Intent Classification',
      'Evaluation Harness', 'AI Guardrails', 'Prompt Engineering', 'OCI', 'Python', 'FastAPI'
    ]
  },
  {
    id: 'trainee-ai-engineer',
    title: 'Trainee AI Engineer',
    duration: 'Aug 2025 - Jul 2026',
    current: false,
    description:
      'Worked on enterprise AI solutions for banking platforms, leveraging Agentic AI, Multi-Agent Systems, MCP integrations, and LLM-powered workflows to enable intelligent data access, automation, and business insights.',
    highlights: [
      'Built multi-agent workflows with LangGraph and LangChain for data retrieval and automation tasks',
      'Developed MCP integrations that connect AI agents to enterprise applications, APIs, and data sources',
      'Learned agentic AI patterns such as ReAct and retrieval-augmented generation (RAG) by building real LLM workflows'
    ],
    technologies: ['Python', 'LangChain', 'LangGraph', 'ReAct', 'MCP', 'RAG', 'FastAPI', 'Docker', 'Oracle']
  },
  {
    id: 'sde-intern',
    title: 'SDE Intern',
    duration: 'Jan 2025 - Jul 2025',
    current: false,
    description: 'Built backend modules and middleware for enterprise applications.',
    highlights: [
      'Developed REST APIs with Spring Boot',
      'Integrated 3rd-party APIs into middleware',
      'Tested and documented APIs using Postman'
    ],
    technologies: ['Java', 'Spring Boot', 'JPA', 'PL/SQL', 'Postman']
  }
];

const education = {
  degree: 'Bachelor of Technology',
  field: 'Computer Science and Engineering (Honors)',
  school: 'KL University',
  location: 'Vaddeswaram, Andhra Pradesh, India',
  years: '2021–2025',
  cgpa: '9.45',
  note: 'PR Director – School of Competitive Coding (SOCC)'
};

const Experience = () => {
  const rolesRef = useRef(null);
  const sectionRef = useRef(null);
  // The "current role" dot pulses forever, so it pauses while this section is off screen
  const onScreen = useOnScreen(sectionRef);
  const reduceMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: rolesRef,
    offset: ['start 75%', 'end 55%']
  });
  const railFill = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });

  // One entrance pattern for the whole section; disabled for reduced-motion users.
  const rise = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.2 },
          transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }
        };

  const pathSteps = [...roles].reverse();

  return (
    <section ref={sectionRef} className={`experience-section ${onScreen ? '' : 'is-paused'}`} id="experience">
      <div className="experience-container">
        <SectionHeader
          eyebrow="Career"
          lead="Experience &"
          accent="Education"
          subtitle="My professional journey and academic background"
          {...rise()}
        />

        <div className="exp-layout">
          <aside className="exp-aside">
            {/* Company + progression */}
            <motion.div className="exp-card exp-company" {...rise()}>
              <div className="exp-company-top">
                <span className="exp-monogram" aria-hidden="true">{company.initial}</span>
                <div>
                  <h3 className="exp-company-name">{company.name}</h3>
                  <p className="exp-meta">
                    <MapPin size={14} />
                    {company.location}
                  </p>
                </div>
              </div>

              <div className="exp-tenure">
                <span className="exp-eyebrow">Tenure</span>
                <span className="exp-tenure-value">{formatTenure()}</span>
                <span className="exp-tenure-range">Jan 2025 – Present</span>
              </div>

              <div className="exp-path-wrap">
                <span className="exp-eyebrow">Growth path</span>
                <ol className="exp-path">
                  {pathSteps.map((role) => (
                    <li key={role.id} className={role.current ? 'is-current' : ''}>
                      {role.title}
                    </li>
                  ))}
                </ol>
              </div>
            </motion.div>

            {/* Education */}
            <motion.div className="exp-card exp-edu" {...rise(0.1)}>
              <div className="exp-edu-top">
                <span className="exp-edu-icon" aria-hidden="true">
                  <GraduationCap size={18} />
                </span>
                <span className="exp-eyebrow">Education</span>
              </div>
              <h3 className="exp-edu-degree">{education.degree}</h3>
              <p className="exp-edu-field">{education.field}</p>
              <p className="exp-meta">
                <MapPin size={14} />
                {education.school} · {education.location}
              </p>

              <div className="exp-edu-stats">
                <div>
                  <strong>{education.cgpa}</strong>
                  <span>CGPA / 10</span>
                </div>
                <div>
                  <strong>{education.years}</strong>
                  <span>Batch</span>
                </div>
              </div>

              <p className="exp-edu-note">{education.note}</p>
            </motion.div>
          </aside>

          {/* Roles timeline */}
          <div className="exp-roles" ref={rolesRef}>
            <div className="exp-rail" aria-hidden="true">
              <motion.div
                className="exp-rail-fill"
                style={{ scaleY: reduceMotion ? 1 : railFill }}
              />
            </div>

            {roles.map((role, index) => (
              <motion.article
                key={role.id}
                className={`exp-role ${role.current ? 'is-current' : ''}`}
                {...rise(index * 0.08)}
              >
                <span className="exp-dot" aria-hidden="true" />

                <div className="exp-card exp-role-card">
                  <header className="exp-role-head">
                    <div>
                      <h3 className="exp-role-title">{role.title}</h3>
                      <p className="exp-meta">
                        <Calendar size={14} />
                        {role.duration}
                      </p>
                    </div>
                    <span className={`exp-status ${role.current ? 'is-current' : ''}`}>
                      {role.current && <i aria-hidden="true" />}
                      {role.current ? 'Current' : 'Completed'}
                    </span>
                  </header>

                  <p className="exp-role-desc">{role.description}</p>

                  <ul className="exp-points">
                    {role.highlights.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>

                  <ul className="exp-tech" aria-label={`${role.title} technologies`}>
                    {role.technologies.map((tech) => (
                      <li key={tech}>{tech}</li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
